import RoundedContainerWithBadgedHeader from "../common/containers/roundedContainerWithBadgedHeader";
import {
    CalendarDaysIcon,
    ListBulletIcon,
    ShieldCheckIcon,
    CheckIcon,
    ExclamationCircleIcon,
    QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { FormattedMessage } from "react-intl";
import EmailUnverifiedPrompt from "@/components/common/modals/emailUnverifiedPrompt";
import { usePage } from "@inertiajs/react";
import ColorLink from "../common/links/colorLink";
import LoginModal from "./loginModal";
import { OpportunityTypeString } from "../../types/opportunity";
import { needsCompleteRegistration } from "@/utils/registration";
import Button from "../common/buttons/button";
import { useState } from "react";
import { getApplyLabel } from "./mobileApplyButton";

interface EligibilityCheckerInterface {
    opportunity: {
        typeTag: OpportunityTypeString;
        slug: string;
        eligibility_criteria: boolean;
    };
    criteria: false | { label: string; eligible: boolean }[];
    eligible: boolean;
    setCompleteProfileOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const LoginWrapper = ({ opportunityType, slug }) => {
    const [open, setOpen] = useState(false);
    return (
        <>
            <LoginModal open={open} setOpen={setOpen} slug={slug} />
            <p className="text-sm">
                <FormattedMessage id="To register for this opportunity you need to be logged into your Voice account. If you haven't got an account, you will be prompted to create one." />
            </p>
            <Button
                color="voice"
                onClick={() => setOpen(true)}
                className="w-full justify-center"
            >
                <FormattedMessage id={getApplyLabel(opportunityType)} />
            </Button>
        </>
    );
};

const ApplyButton = ({ slug, children, criteria, setCompleteProfileOpen }) => {
    const { auth } = usePage().props;

    if (needsCompleteRegistration(auth?.user, criteria)) {
        return (
            <Button
                color="voice"
                onClick={() => setCompleteProfileOpen(true)}
                className="w-full justify-center"
            >
                {children}
            </Button>
        );
    } else {
        return (
            <ColorLink
                color="voice"
                href={route("opportunity-application", slug)}
                className="w-full justify-center"
            >
                {children}
            </ColorLink>
        );
    }
};

export default function EligibilityChecker({
    opportunity,
    criteria,
    eligible,
    setCompleteProfileOpen,
}: EligibilityCheckerInterface) {
    const { auth } = usePage().props;
    if (opportunity.typeTag === "event") {
        return (
            <RoundedContainerWithBadgedHeader
                Icon={CalendarDaysIcon}
                title="Don't miss out!"
            >
                <div className="pt-4 space-y-4">
                    {auth.user ? (
                        <>
                            <p className="text-sm">
                                <FormattedMessage id="Book your place now to secure your place at this event." />
                            </p>
                            {auth.user.emailUnverified ? (
                                <EmailUnverifiedPrompt buttonText="Register" />
                            ) : (
                                <ApplyButton
                                    slug={opportunity.slug}
                                    criteria={criteria}
                                    setCompleteProfileOpen={
                                        setCompleteProfileOpen
                                    }
                                >
                                    <FormattedMessage id="Register" />
                                </ApplyButton>
                            )}
                        </>
                    ) : (
                        <LoginWrapper
                            opportunityType={opportunity.typeTag}
                            slug={opportunity.slug}
                        />
                    )}
                </div>
            </RoundedContainerWithBadgedHeader>
        );
    }
    if (opportunity.typeTag === "survey") {
        return (
            <RoundedContainerWithBadgedHeader
                Icon={ListBulletIcon}
                title="Just to let you know"
            >
                <div className="pt-4 space-y-4">
                    {auth.user ? (
                        <>
                            <p className="text-sm">
                                <FormattedMessage id="Surveys are hosted on third party sites. Any information you provide to Voice as part of the registration process is for you to simply gain access." />
                            </p>
                            <p className="text-sm">
                                <FormattedMessage id="Access to, and use of, any information that you submit as part of the survey is not managed by Voice." />
                            </p>
                            {auth.user.emailUnverified ? (
                                <EmailUnverifiedPrompt buttonText="Register" />
                            ) : (
                                <ApplyButton
                                    slug={opportunity.slug}
                                    criteria={criteria}
                                    setCompleteProfileOpen={
                                        setCompleteProfileOpen
                                    }
                                >
                                    <FormattedMessage id="Start survey" />
                                </ApplyButton>
                            )}
                        </>
                    ) : (
                        <LoginWrapper
                            opportunityType={opportunity.typeTag}
                            slug={opportunity.slug}
                        />
                    )}
                </div>
            </RoundedContainerWithBadgedHeader>
        );
    }
    return (
        <RoundedContainerWithBadgedHeader
            Icon={ShieldCheckIcon}
            title="Eligibility Checker"
        >
            {criteria && (
                <div className="py-6 border-b border-b-gray-300">
                    <ul className="space-y-2">
                        {criteria.map((criterion) => (
                            <li
                                className="flex items-center text-base font-light capitalize"
                                key={criterion.label}
                            >
                                {auth.user ? (
                                    criterion["eligible"] ? (
                                        <CheckIcon className="text-green-500 mr-2 size-6" />
                                    ) : (
                                        <ExclamationCircleIcon className="text-rose-600 mr-2 size-6" />
                                    )
                                ) : (
                                    <QuestionMarkCircleIcon className="text-gray-500 mr-2 size-6" />
                                )}
                                {criterion["label"]}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <div
                className={`${
                    opportunity.eligibility_criteria ? "pt-6" : "pt-4"
                } space-y-4`}
            >
                {auth.user ? (
                    <>
                        {eligible ? (
                            <>
                                <p className="text-sm">
                                    <span className="font-semibold">
                                        <FormattedMessage id="Great!" />
                                    </span>{" "}
                                    <FormattedMessage id="It looks like you're eligible to take part in this opportunity." />
                                </p>
                                <p className="text-sm">
                                    <FormattedMessage id="Please bear in mind that this doesn't guarantee your space and that all selection is made by the researcher hosting the opportunity." />
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="text-sm">
                                    <span className="font-semibold">
                                        <FormattedMessage id="Sorry!" />
                                    </span>{" "}
                                    <FormattedMessage id="It looks like you may not be eligible to take part in this opportunity." />
                                </p>
                                <p className="text-sm">
                                    <FormattedMessage id="You are free to apply, bear in mind however that all selection is made by the researcher hosting the opportunity." />
                                </p>
                            </>
                        )}
                        {auth.user.emailUnverified ? (
                            <EmailUnverifiedPrompt buttonText="Apply now" />
                        ) : (
                            <ApplyButton
                                slug={opportunity.slug}
                                criteria={criteria}
                                setCompleteProfileOpen={setCompleteProfileOpen}
                            >
                                <FormattedMessage id="Apply now" />
                            </ApplyButton>
                        )}
                    </>
                ) : (
                    <LoginWrapper
                        opportunityType={opportunity.typeTag}
                        slug={opportunity.slug}
                    />
                )}
            </div>
        </RoundedContainerWithBadgedHeader>
    );
}
